import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogContent, MatDialogRef } from "@angular/material/dialog";
import { Subscription } from "rxjs";

import { IncidenciaDeEtapa } from "../etapas-versiones.model";
import { EtapasVersionesService } from "../etapas-versiones.service";
import {
  FILTROS_VINCULAR,
  FiltroVincular,
  IncidenciasDeLaConsulta,
  claseEstatus,
  clasePrioridad,
  filtrarParaVincular,
  nombrePrioridad,
  textoFolios,
} from "../incidencias-prueba.util";
import { cierrePorTeclaOFondo, configDialogoPruebas } from "../pruebas-dialogo";

/** Lo que recibe «Vincular incidencia existente». */
export interface DatosVincularIncidencia {
  idProyecto: string;
  idEtapa: string;
  /** La versión que se está probando (el filtro «Detectadas en esta versión»). */
  idVersion: string | null;
  nombreProyecto?: string | null;
  nombreEtapa?: string | null;
  /** «V3-13-08-2026 · Regresión 2». */
  contexto?: string | null;
  /** Las que la ejecución ya tiene: salen marcadas y no se eligen otra vez. */
  vinculadas: readonly number[];
  /** Las incidencias de la etapa si T2 ya las leyó; sin ellas se consultan. */
  consulta?: IncidenciasDeLaConsulta | null;
  /** El nombre de cada versión que se conoce, por su id (columna «Versión detectada»). */
  nombresVersion: Readonly<Record<string, string>>;
  /** Si nombresVersion trae todas las de la etapa: una que no esté ahí no es de la etapa. */
  todasLasVersiones: boolean;
}

/** Lo que da al cerrar: las elegidas (vacío si se canceló) y la consulta, para no repetirla. */
export interface ResultadoVincularIncidencia {
  seleccionadas: IncidenciaDeEtapa[];
  consulta: IncidenciasDeLaConsulta | null;
}

/** Un renglón de la tabla, ya con sus textos. */
interface FilaVincular {
  incidencia: IncidenciaDeEtapa;
  vinculada: boolean;
  prioridad: string;
  clasePrioridad: string;
  claseEstatus: string;
  version: string;
}

const ID_TITULO = "t2v-titulo";

/** Abre «Vincular incidencia existente» encima de T2. */
export function abrirVincularIncidencia(
  dialog: MatDialog,
  datos: DatosVincularIncidencia,
): MatDialogRef<VincularIncidenciaComponent, ResultadoVincularIncidencia> {
  return dialog.open<VincularIncidenciaComponent, DatosVincularIncidencia, ResultadoVincularIncidencia>(
    VincularIncidenciaComponent,
    configDialogoPruebas(datos, ID_TITULO, "920px"),
  );
}

/**
 * «Vincular incidencia existente» desde T2 (plan de etapas y versiones
 * §5.7, maqueta T2v): las incidencias de la etapa con el filtro del tablero
 * de QA (CONSINCXFILTROQA: proyecto, etapa y tenant), filtradas aquí por
 * «Detectadas en esta versión», «Abiertas de la etapa» (D1) o «Todas», y
 * buscadas por folio o título. Las que la ejecución ya tiene salen marcadas.
 * El servidor vuelve a validar al guardar la ejecución que cada una sea del
 * proyecto, la etapa y el tenant.
 */
@Component({
  selector: "app-vincular-incidencia",
  standalone: true,
  templateUrl: "./vincular-incidencia.component.html",
  styleUrls: ["../pruebas-dialogo.css", "./vincular-incidencia.component.css"],
  imports: [CommonModule, MatDialogContent, MatDialogActions],
})
export class VincularIncidenciaComponent implements OnInit, OnDestroy {
  readonly idTitulo = ID_TITULO;
  readonly filtros = FILTROS_VINCULAR;
  readonly subtitulo: string;

  filtro: FiltroVincular = "abiertas";
  busqueda = "";
  filas: FilaVincular[] = [];
  cargando = false;
  consulta: IncidenciasDeLaConsulta | null;

  private readonly vinculadas: ReadonlySet<number>;
  private readonly seleccion = new Map<number, IncidenciaDeEtapa>();
  private cerrado = false;
  private readonly subs: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly datos: DatosVincularIncidencia,
    private readonly dialogRef: MatDialogRef<VincularIncidenciaComponent, ResultadoVincularIncidencia>,
    private readonly servicio: EtapasVersionesService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.subtitulo = [datos?.nombreProyecto, datos?.nombreEtapa, datos?.contexto]
      .map((p) => (p ?? "").trim())
      .filter(Boolean)
      .join(" · ");
    this.vinculadas = new Set(datos?.vinculadas ?? []);
    this.consulta = datos?.consulta?.error ? null : (datos?.consulta ?? null);
  }

  ngOnInit(): void {
    this.subs.push(...cierrePorTeclaOFondo(this.dialogRef, () => this.cancelar()));
    if (this.consulta) {
      this.aplicar();
    } else {
      this.cargar();
    }
  }

  ngOnDestroy(): void {
    this.cerrado = true;
    this.subs.forEach((s) => s.unsubscribe());
  }

  /* ============================ Lo que pinta la plantilla ============================ */

  get error(): string | null {
    return this.consulta?.error ?? null;
  }

  get cuantasSeleccionadas(): number {
    return this.seleccion.size;
  }

  /** «#33 y #35». */
  get foliosSeleccionados(): string {
    return textoFolios(Array.from(this.seleccion.values(), (incidencia) => incidencia.folio));
  }

  /** El título de la tabla y del pie, según el filtro. */
  get descripcionFiltro(): string {
    const etapa = this.datos.nombreEtapa || "la etapa";
    switch (this.filtro) {
      case "version":
        return "Incidencias detectadas en esta versión";
      case "todas":
        return `Incidencias de la etapa ${etapa}`;
      default:
        return `Incidencias abiertas de la etapa ${etapa}`;
    }
  }

  /** Cuántas de las que se ven ya estaban vinculadas. */
  get yaVinculadasVisibles(): number {
    return this.filas.filter((fila) => fila.vinculada).length;
  }

  elegida(fila: FilaVincular): boolean {
    return fila.vinculada || this.seleccion.has(fila.incidencia.id);
  }

  trackFila(_: number, fila: FilaVincular): number {
    return fila.incidencia.id;
  }

  /* ============================ Acciones ============================ */

  cargar(): void {
    this.cargando = true;
    this.consulta = null;
    this.pinta();
    this.subs.push(
      this.servicio.incidenciasDeLaEtapa(this.datos.idProyecto, this.datos.idEtapa).subscribe({
        next: (consulta) => {
          this.cargando = false;
          this.consulta = consulta;
          this.aplicar();
        },
        error: () => {
          this.cargando = false;
          this.consulta = { incidencias: [], incompleta: false, error: "No se pudieron consultar las incidencias: sin conexión con el servidor." };
          this.aplicar();
        },
      }),
    );
  }

  cambiarFiltro(filtro: FiltroVincular): void {
    this.filtro = filtro;
    this.aplicar();
  }

  buscar(texto: string): void {
    this.busqueda = texto;
    this.aplicar();
  }

  alternar(fila: FilaVincular): void {
    if (fila.vinculada) {
      return;
    }
    if (this.seleccion.has(fila.incidencia.id)) {
      this.seleccion.delete(fila.incidencia.id);
    } else {
      this.seleccion.set(fila.incidencia.id, fila.incidencia);
    }
    this.pinta();
  }

  vincular(): void {
    if (!this.seleccion.size) {
      return;
    }
    this.cierra({ seleccionadas: Array.from(this.seleccion.values()), consulta: this.consultaLeida() });
  }

  cancelar(): void {
    this.cierra({ seleccionadas: [], consulta: this.consultaLeida() });
  }

  /* ============================ Internas ============================ */

  private aplicar(): void {
    const lista = this.consulta?.incidencias ?? [];
    this.filas = filtrarParaVincular(lista, this.filtro, this.datos.idVersion, this.busqueda).map((incidencia) => ({
      incidencia,
      vinculada: this.vinculadas.has(incidencia.id),
      prioridad: nombrePrioridad(incidencia.prioridad),
      clasePrioridad: clasePrioridad(incidencia.prioridad),
      claseEstatus: claseEstatus(incidencia.estatus),
      version: this.nombreVersion(incidencia.version),
    }));
    this.pinta();
  }

  private nombreVersion(id: string | null): string {
    if (!id) {
      return "—";
    }
    const nombre = this.datos.nombresVersion?.[id];
    if (nombre) {
      return nombre;
    }
    return this.datos.todasLasVersiones ? "Sin versión identificada" : "Otra versión de la etapa";
  }

  /** Solo una consulta que sí se leyó vale la pena guardarla. */
  private consultaLeida(): IncidenciasDeLaConsulta | null {
    return this.consulta && !this.consulta.error ? this.consulta : null;
  }

  private cierra(resultado: ResultadoVincularIncidencia): void {
    if (this.cerrado) {
      return;
    }
    this.cerrado = true;
    this.dialogRef.close(resultado);
  }

  /** La app corre sin zonas: lo que llega de una petición se pinta a mano. */
  private pinta(): void {
    if (!this.cerrado) {
      this.cdr.detectChanges();
    }
  }
}
